import { getFormattedErr } from 'src/helpers';
import { ArenaExitPrompt } from '../types/Multiplayer';

/**
 * One longer round, not two short ones: people leave at a round boundary, so a
 * second round mostly costs us the players who would have finished the first.
 */
export const MULTIPLAYER_DEFAULT_ROUNDS = 1;
export const MULTIPLAYER_DEFAULT_QUESTIONS = 5;

/** How many online players one wave pulls in. */
export const INVITE_BLAST_PAGE_SIZE = 50;
/** Players asked per wave, taken in order from the one fetched list. */
export const INVITE_BLAST_WAVE_SIZE = 10;
/** Gap between waves — long enough for someone to actually accept. */
export const INVITE_BLAST_WAVE_MS = 12000;
/** After a failed blast, how long before retrying is worth the host's click. */
export const INVITE_RETRY_COOLDOWN_MS = 60000;
/**
 * Grace added to the server's "next AI answer is due in N seconds" before the
 * play screen re-reads progress.
 *
 * The server hands back the exact moment, so this only has to cover clock skew
 * and the round trip. Without it a read can land a hair early, be told to come
 * back for the same answer, and burn a request to learn nothing.
 */
export const PERSONA_REVEAL_BUFFER_MS = 400;

/**
 * How long after the result appears before the host's rematch button arms.
 *
 * Closing a game also walks the AI opponent out of the room, and on the
 * abandoned path that runs in the force-end worker rather than in the request.
 * Starting again before it lands means a lobby still holding a ready opponent
 * from the match just finished, which is enough for the host's auto-start to
 * fire on a game with no rounds left.
 *
 * Spent while the reader is still taking in the scoreboard, not after they have
 * decided to move — the same seconds, but the first is free and the second is a
 * delay. Host only: nobody else can start the next match.
 */
export const REMATCH_ARM_SECONDS = 3;

/**
 * What an AI opponent is, in one line, wherever it is offered.
 *
 * Said plainly because the plain truth is the better pitch: it answers from
 * what real players at the same level did on these exact questions, so it is
 * neither a difficulty dial nor a scripted bot.
 *
 * Deliberately plain English — no idiom, no dashes, no "not a bot" phrasing to
 * decode. Most readers here are not native speakers and a clever line that has
 * to be re-read is a line that does not get read.
 *
 * Numberless on purpose: the level is resolved server-side when the round
 * starts, so no screen that offers the match yet knows which band it will be.
 */
/**
 * The label on every control that starts an AI match.
 *
 * One constant because it had already drifted into three wordings across two
 * screens ("Play an AI instead", "Play an AI opponent", "Play an AI now"), and
 * a control the user is meant to recognise cannot be renamed per surface.
 *
 * "Instantly" is the whole reason to press it -- the alternative is ten seconds
 * of searching -- so it earns its place over "instead", which only makes sense
 * if you already know what it is an alternative to.
 */
export const AI_OPPONENT_CTA = 'Play against AI instantly';

export const AI_OPPONENT_HINT =
  'Plays like a real player at your level, based on how others answered these same questions.';

/**
 * The other way out of an empty search, and the one that finds a human.
 *
 * Named as the host's own skills rather than "widen the search", because what
 * changes is who gets asked: everyone online who plays anything this host
 * plays, instead of only the skill the room was set up on.
 */
export const WIDEN_SEARCH_CTA = 'Search my top skills';

export const WIDEN_SEARCH_HINT =
  "We'll invite players from your top skills and pick one you both know.";

/** Give up after this and offer the host an AI opponent or a shareable link. */
export const INVITE_BLAST_TIMEOUT_MS = 60000;

/** A room a group lives in, or a one-off matchmade quick match. */
export const MULTIPLAYER_ROOM_TYPE = {
  CLASSIC: 'CLASSIC',
  CHALLENGE: 'CHALLENGE',
} as const;

/**
 * `?host=challenge` opens a room and goes to it, the way `?code=` joins one. It
 * exists so a link can start a match — the community feed being the case it is
 * meant for. Answered by Skill Builder and by the multiplayer home, which still
 * carries it for links that named the old address.
 */
export const MULTIPLAYER_HOST_PARAM = 'host';
/**
 * Optional skill for the hosted room. With it the game is created on the way
 * in; without it the arena opens on its setup dialog and the host picks.
 */
export const MULTIPLAYER_HOST_SKILL_PARAM = 'skill';
export const MULTIPLAYER_HOST_PARAM_TYPES: Record<
  string,
  (typeof MULTIPLAYER_ROOM_TYPE)[keyof typeof MULTIPLAYER_ROOM_TYPE]
> = {
  classic: MULTIPLAYER_ROOM_TYPE.CLASSIC,
  challenge: MULTIPLAYER_ROOM_TYPE.CHALLENGE,
};

/**
 * The link that opens a room. One place builds it, so the params stay honest.
 *
 * Aimed at Skill Builder rather than the multiplayer home: both pages answer
 * these params, and routing someone through a page they never asked for just to
 * be redirected out of it is a flash of the wrong product.
 */
export const hostRoomHref = (type: 'classic' | 'challenge', skillId?: string) =>
  `/skill-builder?${MULTIPLAYER_HOST_PARAM}=${type}${
    skillId ? `&${MULTIPLAYER_HOST_SKILL_PARAM}=${skillId}` : ''
  }`;

/**
 * How long into a round invites are still offered.
 *
 * Deliberately half the server's MULTIPLAYER_LATE_JOIN_SECONDS: an invite sent
 * on the last second of this window still leaves a full minute to accept it.
 */
export const MULTIPLAYER_INVITE_WINDOW_MS = 60_000;

/** How long the arena searches before it starts suggesting you invite a friend. */
export const CHALLENGE_SEARCH_MS = 10_000;
/** Total time the arena keeps sending invites while the dialog is open. */
export const CHALLENGE_INVITE_TOTAL_MS = 30_000;
/**
 * Seconds a late joiner gets before being dropped into a live round.
 *
 * Shorter than a normal start: the round is already running, so every second
 * here is one they play without. Long enough to say what is happening.
 */
export const CHALLENGE_JOIN_COUNT = 3;

/** Seconds between "everyone is ready" and the first question. */
export const CHALLENGE_START_COUNT = 3;

/** Host included. Past this the lobby stops offering invites. */
export const MAX_ROOM_PLAYERS = 4;

/** Reading time for the round standings before the next round runs itself. */
export const CHALLENGE_NEXT_ROUND_SECONDS = 10;

/** Enough to prove people are here without turning the rail into a list page. */
export const VISIBLE_PLAYERS = 3;

/**
 * How long an invite toast stays up, counting only time the tab is on screen.
 *
 * The host's own invite window: past it they have usually started against the
 * AI, and a join from here lands on "that room is mid-match".
 */
export const INVITE_TOAST_TIMEOUT_MS = CHALLENGE_INVITE_TOTAL_MS;

/**
 * The same, for a round already running. Invites into one stop a minute in
 * (MULTIPLAYER_INVITE_WINDOW_MS) and the server takes late joiners for two, so
 * the last invite sent still has a minute to be answered.
 */
export const LIVE_INVITE_TOAST_TIMEOUT_MS = 60_000;

/**
 * An invite that sat in a background tab is gone after this, however little of
 * it was seen: two minutes is the server's late-join window, the longest any
 * invite can still be honoured.
 */
export const INVITE_TOAST_MAX_AGE_MS = 120_000;

/** Radar ring geometry, in the SVG's own viewBox units. */
export const RADAR_RING_RADIUS = 47;

/** Positions of the four anonymous blips on the radar's outer ring. */
export const RADAR_DOT_POSITIONS = [
  { top: -5, left: '50%', marginLeft: -5 },
  { right: -5, top: '50%', marginTop: -5 },
  { bottom: -5, left: '50%', marginLeft: -5 },
  { left: -5, top: '50%', marginTop: -5 },
] as const;

/**
 * Why a join was refused, in words.
 *
 * The server's reason is a snake_case key; the generic formatter turns it into
 * "Room Already In Game", which reads like a crash. These are the rejections a
 * person can actually do something about, so they get a sentence — and every
 * join path shares them rather than only the code dialog, which is where they
 * used to live.
 */
export const JOIN_ERROR_MESSAGES: Record<string, string> = {
  room_already_in_game:
    'That room is mid-match. Ask the host to invite you when the round ends.',
  host_left_the_room:
    'The host has left, so this challenge is over. Start a new match instead.',
  user_blocked_from_room: 'You can no longer join that room.',
  no_active_room_found:
    "That code doesn't match a room. Check it and try again.",
};

/**
 * These maps only cover the rejections worth a sentence. Anything unmapped falls
 * through to the raw server key rather than a generic "try again" — a title-cased
 * key is ugly, but it names the actual cause, and a message that hides which
 * error happened is the harder bug to report and the harder one to fix.
 */
const messageFor = (map: Record<string, string>, key?: string | null) =>
  map[key ?? ''] ?? getFormattedErr(key);

export const joinErrorMessage = (key?: string | null) =>
  messageFor(JOIN_ERROR_MESSAGES, key);

/** Why accepting an AI player's invite did not put you in a game. */
export const AI_INVITE_ERROR_MESSAGES: Record<string, string> = {
  ...JOIN_ERROR_MESSAGES,
  ai_invite_not_found: 'That invite has ended.',
  no_questions_found:
    'This skill cannot start a game right now. Try another skill.',
  no_persona_available:
    'No AI player is free right now. Try again in a minute.',
};

export const aiInviteErrorMessage = (key?: string | null) =>
  messageFor(AI_INVITE_ERROR_MESSAGES, key);

/**
 * An AI player's invite, in the same plain English as AI_OPPONENT_HINT. It says
 * how many others were invited, never who, and never that anyone has joined:
 * when the toast is drawn nobody has.
 */
export const AI_INVITE_EYEBROW = 'AI game invite';
export const AI_INVITE_MUTE_LABEL = 'No AI invites today';
export const aiInviteOthersLine = (others: number) =>
  others === 1
    ? '1 other learner got this invite.'
    : `${others} other learners got this invite.`;

/**
 * Join refusals that mean "this match can no longer take you". Opened from a
 * notification, these get the start-your-own dialog rather than a toast.
 */
export const INVITE_ENDED_JOIN_ERRORS = [
  'no_active_room_found',
  'room_already_in_game',
  'host_left_the_room',
];

/** Why a round could not begin. */
export const START_ERROR_MESSAGES: Record<string, string> = {
  need_another_ready_player:
    'Nobody else has joined yet. A challenge needs two.',
  no_persona_available: 'No AI opponent free right now. Try the invite link.',
  persona_only_in_challenge: 'AI opponents are quick-match only.',
  not_enough_ai_credits:
    'A custom-topic round needs AI credits, and yours have run out.',
};

/** Named so the toast above and the top-up dialog cannot key off different strings. */
export const NOT_ENOUGH_AI_CREDITS = 'not_enough_ai_credits';

export const startErrorMessage = (key?: string | null) =>
  messageFor(START_ERROR_MESSAGES, key);

/**
 * Why a nudge did not go through.
 *
 * Some arrive as a thrown error, some as `{ success: false, msg }` on a 200, so
 * both paths read the same map rather than each title-casing the raw key.
 */
export const NUDGE_ERROR_MESSAGES: Record<string, string> = {
  finish_your_round_to_nudge: 'Finish your round first.',
  rate_limit_try_again_later: 'Just nudged them. Give it a few seconds.',
  user_already_ready: 'They are already ready.',
  cannot_nudge_self: "That's you.",
  no_active_game: 'That round is already over.',
};

export const nudgeErrorMessage = (key?: string | null) =>
  messageFor(NUDGE_ERROR_MESSAGES, key);

/**
 * Readying up in the second a round is being dealt: the server says so rather
 * than turning the player away, and the ready call is simply tried again. Not
 * an error, so it is shown as one line of info while that happens.
 */
export const ROUND_STARTING_RETRY = 'round_starting_retry';
export const ROUND_STARTING_RETRY_DELAY_MS = 2000;
export const ROUND_STARTING_RETRY_ATTEMPTS = 3;
export const ROUND_STARTING_MESSAGE = 'The round is starting. Getting you in…';
export const ROUND_STARTING_GAVE_UP_MESSAGE =
  'The round started without you. You can join the next one.';

/**
 * What a nudge means to the person who receives it.
 *
 * The same event lands on four different people, and one sentence cannot serve
 * them: telling a host on the result screen to "tap Ready" names a control that
 * is not on their screen and hides the thing they are actually being asked for.
 */
export const nudgeReceivedMessage = ({
  isPlaying,
  isHost,
  inLobby,
  from,
}: {
  /** Mid-round, with questions left to answer. */
  isPlaying: boolean;
  isHost: boolean;
  /** Looking at the room itself rather than a result or another screen. */
  inLobby: boolean;
  from?: string | null;
}) => {
  const who = from?.trim();

  if (isPlaying)
    return who
      ? `${who} is waiting on your answer. Keep the match moving.`
      : 'Answer and keep the match moving.';

  if (isHost)
    return inLobby
      ? `${who ?? 'Everyone'} is ready and waiting for you to start.`
      : `${who ?? 'Someone'} is waiting in the lobby. Start the next match.`;

  // Naming a control the reader cannot see is worse than saying nothing: on the
  // result screen the way back in is "Play again", not a ready toggle.
  return inLobby
    ? `${who ? `${who} is ready. ` : ''}Tap Ready so the next round can start.`
    : `${who ? `${who} is waiting. ` : ''}Tap Play again to join the next match.`;
};

export const arenaExitPrompt = ({
  isPlaying,
  hasOthers,
  isWaiting = false,
  isMatchmaking = false,
}: {
  /** Mid-round, with questions still unanswered. */
  isPlaying: boolean;
  /** Anyone in the room besides you. */
  hasOthers: boolean;
  /** Your round is in, the others are still answering. */
  isWaiting?: boolean;
  /** Invites are going out right now. */
  isMatchmaking?: boolean;
}): ArenaExitPrompt => {
  if (isPlaying)
    return {
      eyebrow: 'Hold on',
      title: 'Round is going on',
      description:
        'Only a few questions are left. If you go now, you lose those points and the other player takes the lead and the winner bonus.',
      confirmText: 'Leave anyway',
      cancelText: 'Keep playing',
      emphasis: 'cancel',
    };

  // Nothing left to play, but the result does not exist until everyone is in —
  // and there is a nudge on this very screen for exactly this.
  if (isWaiting)
    return {
      eyebrow: 'Almost there',
      title: 'Result is not out yet',
      description:
        'Your answers are in. Wait for the others to finish and the result will show here — you can nudge them if they are taking too long.',
      confirmText: 'Leave anyway',
      cancelText: 'Wait for result',
      emphasis: 'cancel',
    };

  if (hasOthers)
    return {
      eyebrow: 'Hold on',
      title: 'Match is ready',
      description:
        'The other player is waiting for you. One round takes about 2 minutes. Finish it, then you can leave.',
      confirmText: 'Leave anyway',
      cancelText: 'Stay',
      emphasis: 'cancel',
    };

  // Nobody here yet, but people are being pinged about a room this player is
  // about to walk out of — worth saying, and worth waiting a moment for.
  if (isMatchmaking)
    return {
      eyebrow: 'Hold on',
      title: 'Players are being invited',
      description:
        'We are inviting players to your match right now. Wait a few seconds — someone may join any moment.',
      confirmText: 'Leave anyway',
      cancelText: 'Keep waiting',
      emphasis: 'cancel',
    };

  // A match is set up but nobody has been asked and nobody is here, so there is
  // nothing to argue for — just confirm and go.
  return {
    eyebrow: 'Leaving',
    title: 'Leave this room?',
    description:
      'You will be removed from the room. You can start a new match any time.',
    confirmText: 'Leave room',
    cancelText: 'Stay',
    emphasis: 'cancel',
  };
};

export const QUESTION_COUNTS = [5, 7, 10] as const;

export const ROUND_COUNTS = [1, 3, 5] as const;

/** Matches Classic's own floor, so the same text is accepted on both surfaces. */
export const MIN_CUSTOM_TOPIC_LENGTH = 5;

/**
 * Safety net for a held start: how long before asking again whether the
 * questions landed. The socket is the mechanism; this only covers the emit
 * being missed -- a dropped connection, or a tab the browser froze.
 *
 * Deliberately longer than the work it watches. A curriculum lands in about ten
 * seconds and its questions in another fifteen, and the server is already
 * writing both -- asking again inside that window spends a request to be told
 * what we know, which is that it is still going.
 */
export const QUESTIONS_RETRY_MS = 45000;

/**
 * How long a player who is only watching the round being built keeps the notice
 * up. They have no start to retry, so this is the one way their copy closes if
 * the finishing event never reaches them.
 */
export const QUESTIONS_WATCH_TIMEOUT_MS = 90000;

/**
 * How long a host may hold a start before the arena gives up. Longer than the
 * watcher's bound — the host is waiting on a curriculum and then a round of
 * questions — but bounded, because every retry re-queues both.
 */
export const QUESTIONS_HOLD_TIMEOUT_MS = 240000;

/** Given up on: the topic never produced a round. */
export const questionsHoldExpiredMessage =
  'That topic is taking too long to build. Try starting again, or pick another.';

/** Topics you have played before, offered back. A reminder, not a menu. */
export const PAST_TOPIC_SUGGESTIONS = 3;

/**
 * The same behaviour in a Classic room, measured against its own chrome.
 *
 * Classic carries a top bar (and mobile tabs) where the arena carries its HUD,
 * and the panel group it sits in has padding the arena's stage does not. Both
 * are written to CSS variables by the surfaces that own them, so this is the
 * same subtraction as the arena's, term for term.
 */
export const CLASSIC_PLAY_LAYOUT = {
  layout: 'fit',
  fitOffset: `calc(${[
    'var(--classic-head, 72px)',
    'var(--classic-stage-pad, 64px)',
  ].join(' + ')})`,
} as const;

/**
 * The duel plays at one height.
 *
 * A tall question used to scroll the HUD's clock and points off the top and
 * leave the forward button at a different place on every question. Held to the
 * viewport, the head and the action bar stay where the eye last left them and
 * only the question body moves — which on a phone also means the button is
 * always under the thumb.
 *
 * Everything the card cannot have: the sticky HUD as measured onto
 * --challenge-head, and the stage padding it sits inside. Neither app bar is
 * subtracted — this route hides the navbar outright (NAVBAR_HIDDEN_LINKS) and
 * the mobile bottom bar has its own allowlist — and the room reserved for them
 * was a band of dead screen under the card, right where the answer button
 * wants to be.
 */
export const CHALLENGE_PLAY_LAYOUT = {
  layout: 'fit',
  fitOffset: `calc(${[
    'var(--challenge-head, 116px)',
    'var(--challenge-stage-pad, 48px)',
    // The phone's room handle bar, set on ChallengeRoot; 0 elsewhere.
    'var(--sticky-bottom-offset, 0px)',
  ].join(' + ')})`,
} as const;

/** Room chat in a Challenge is kept to a line or two. */
export const CHALLENGE_CHAT_MAX_LENGTH = 200;

export const CHALLENGE_CHAT_QUICK_REPLIES = [
  'Well played',
  'Rematch?',
  'Close one!',
  'Good Game',
] as const;
